import { z } from "zod";
import {
  ChapterAudioSummarySchema,
  ChapterLockSchema,
  FailureSchema,
  IsoDateTimeSchema,
} from "./common";
import {
  BookIdSchema,
  ChapterIdSchema,
  InstructionIdSchema,
  ScriptVersionIdSchema,
  Sha256Schema,
} from "./ids";
import { JobSummarySchema } from "./jobs";
import { ManuscriptNodeSchema } from "./manuscript";
import { BlockSchema } from "./script";
import { ChapterStateSchema, ScriptSourceSchema } from "./states";

/** A Manuscript node on the left of Review, with the markers stripped for the diff. */
export const AlignedNodeSchema = ManuscriptNodeSchema.extend({
  strippedText: z.string(),
});
export type AlignedNode = z.infer<typeof AlignedNodeSchema>;

/**
 * One row of the Review grid: a Block and the nodes it was made from.
 * `strippedSourceText` is the nodes' text joined by blank lines with the
 * italic and bold markers removed, the "before" side of the word diff.
 */
export const AlignmentPairSchema = z.strictObject({
  block: BlockSchema,
  nodes: z.array(AlignedNodeSchema),
  strippedSourceText: z.string(),
});
export type AlignmentPair = z.infer<typeof AlignmentPairSchema>;

/** A node no Block covers; rendered as a warn row after Block `afterBlockIndex` (null: before the first). */
export const DroppedNodeSchema = AlignedNodeSchema.extend({
  afterBlockIndex: z.int().nonnegative().nullable(),
});
export type DroppedNode = z.infer<typeof DroppedNodeSchema>;

/** Computed server-side per version: the header line "+14 −6 · footnotes 2 · figures 1 · furniture removed 3". */
export const ScriptStatsSchema = z.strictObject({
  blocks: z.int().nonnegative(),
  characters: z.int().nonnegative(),
  wordsAdded: z.int().nonnegative(),
  wordsRemoved: z.int().nonnegative(),
  footnotes: z.int().nonnegative(),
  figures: z.int().nonnegative(),
  furnitureRemoved: z.int().nonnegative(),
  tones: z.int().nonnegative(),
  droppedNodes: z.int().nonnegative(),
  oversizedBlocks: z.int().nonnegative(),
  undersizedBlocks: z.int().nonnegative(),
});
export type ScriptStats = z.infer<typeof ScriptStatsSchema>;

export const ScriptVersionSummarySchema = z.strictObject({
  id: ScriptVersionIdSchema,
  /** 1-based, in creation order. */
  number: z.int().positive(),
  source: ScriptSourceSchema,
  createdAt: IsoDateTimeSchema,
  instruction: z.strictObject({ id: InstructionIdSchema, text: z.string() }).nullable(),
  parentVersionId: ScriptVersionIdSchema.nullable(),
  isCurrent: z.boolean(),
  /** This version is the live Approved copy the Render reads. */
  isApproved: z.boolean(),
  blockCount: z.int().nonnegative(),
  characterCount: z.int().nonnegative(),
  editedBlocks: z.int().nonnegative(),
  styleSheetVersion: z.int().positive().nullable(),
});
export type ScriptVersionSummary = z.infer<typeof ScriptVersionSummarySchema>;

export const ApprovalSchema = z.strictObject({
  versionId: ScriptVersionIdSchema,
  approvedAt: IsoDateTimeSchema,
  contentHash: Sha256Schema,
  characterCount: z.int().nonnegative(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

export const ScriptViewSchema = z.strictObject({
  version: ScriptVersionSummarySchema,
  blocks: z.array(BlockSchema),
  pairs: z.array(AlignmentPairSchema),
  droppedNodes: z.array(DroppedNodeSchema),
  stats: ScriptStatsSchema,
});
export type ScriptView = z.infer<typeof ScriptViewSchema>;

/**
 * GET /books/:id/chapters/:cid[?versionId]. `script` is null while the Chapter
 * is pending, refining, excluded, or failed before its first script; then
 * `activeJob` or `failure` says why.
 */
export const ChapterReviewSchema = z.strictObject({
  bookId: BookIdSchema,
  chapterId: ChapterIdSchema,
  position: z.int().positive(),
  number: z.int().positive().nullable(),
  title: z.string(),
  state: ChapterStateSchema,
  excluded: z.boolean(),
  isCalibration: z.boolean(),
  nodeCount: z.int().nonnegative(),
  script: ScriptViewSchema.nullable(),
  /** Every version, newest first; restore targets one of these. */
  versions: z.array(ScriptVersionSummarySchema),
  approval: ApprovalSchema.nullable(),
  audio: ChapterAudioSummarySchema.nullable(),
  lock: ChapterLockSchema.nullable(),
  failure: FailureSchema.nullable(),
  activeJob: JobSummarySchema.nullable(),
});
export type ChapterReview = z.infer<typeof ChapterReviewSchema>;

export const ReviewQuerySchema = z.strictObject({
  versionId: ScriptVersionIdSchema.optional(),
});
export type ReviewQuery = z.infer<typeof ReviewQuerySchema>;

export const VersionsResponseSchema = z.strictObject({
  chapterId: ChapterIdSchema,
  versions: z.array(ScriptVersionSummarySchema),
});
export type VersionsResponse = z.infer<typeof VersionsResponseSchema>;

/**
 * PATCH /books/:id/chapters/:cid/blocks/:bid. Cues are read-only in v1. The
 * first edit of a refinement or restore version forks a new edit version;
 * an Approved Chapter goes back to In review; rendered audio turns stale.
 */
export const BlockPatchSchema = z.strictObject({
  versionId: ScriptVersionIdSchema,
  text: z.string().trim().min(1),
});
export type BlockPatch = z.infer<typeof BlockPatchSchema>;

export const ApproveRequestSchema = z.strictObject({
  versionId: ScriptVersionIdSchema,
  /** Without this, a script with dropped nodes answers 409 DROPPED_NODES { count }. */
  acknowledgeDroppedNodes: z.boolean().default(false),
});
export type ApproveRequest = z.infer<typeof ApproveRequestSchema>;

export const RerunRequestSchema = z.strictObject({
  /** Recorded in the Style Sheet and applied to every later run; null is a plain re-run. */
  instruction: z.string().trim().min(1).max(2000).nullable(),
});
export type RerunRequest = z.infer<typeof RerunRequestSchema>;

export const RestoreRequestSchema = z.strictObject({
  versionId: ScriptVersionIdSchema,
});
export type RestoreRequest = z.infer<typeof RestoreRequestSchema>;

/** POST /books/:id/chapters/:cid/render. */
export const ChapterRenderRequestSchema = z.strictObject({
  estimateFingerprint: Sha256Schema,
  /** Required (true) when the Chapter already has Chapter Audio; else 409 RERENDER_NOT_CONFIRMED. */
  confirmReRender: z.boolean().default(false),
});
export type ChapterRenderRequest = z.infer<typeof ChapterRenderRequestSchema>;

export const AUDIO_FORMATS = ["mp3", "wav"] as const;
export const AudioQuerySchema = z.strictObject({
  format: z.enum(AUDIO_FORMATS).default("mp3"),
  download: z.literal("1").optional(),
});
export type AudioQuery = z.infer<typeof AudioQuerySchema>;
